<!-- src/components/new-tip-v2/TiersTable.vue -->
<!--
  Saisie des affectations d'une campagne v2 : POS, superviseur (ASM) et les
  3 paliers d'objectifs sur le produit de la campagne.

  Remplace ObjectivesGainsTable de la v1 (qui portait 4 catégories fixes).

  Les règles de cohérence affichées ici sont celles appliquées côté API par
  NewTipV2TierValidator : palier 1 obligatoire, paliers contigus, quantités et
  gains strictement croissants. Elles sont revalidées côté serveur — l'intérêt
  ici est de les signaler avant l'envoi.
-->
<template>
  <div>
    <v-card flat color="grey lighten-5" class="mb-3" v-if="!readonly">
      <v-card-text class="py-2 d-flex align-center flex-wrap" style="gap: 8px">
        <v-btn small outlined @click="dialogBulk = true">
          <v-icon left small>mdi-tune</v-icon>
          Appliquer en masse
        </v-btn>
        <v-btn small outlined color="primary" @click="addRow">
          <v-icon left small>mdi-plus</v-icon>
          Ajouter un POS
        </v-btn>
        <v-spacer />
        <span class="text-caption grey--text">
          {{ rows.length }} affectation{{ rows.length > 1 ? 's' : '' }}
        </span>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="!readonly && invalidRowCount > 0"
      type="warning" dense text class="mb-3"
    >
      {{ invalidRowCount }} affectation{{ invalidRowCount > 1 ? 's' : '' }}
      avec des paliers incohérents — survolez l'icône d'alerte de la ligne
      pour le détail. L'API refusera ces lignes.
    </v-alert>

    <v-data-table
      :headers="tableHeaders"
      :items="rows"
      :items-per-page="50"
      dense
      class="elevation-0 tiers-table"
      :no-data-text="readonly ? 'Aucune affectation' : 'Cliquez sur Ajouter un POS pour commencer'"
    >
      <!-- POS : autocomplete débouncée sur le code -->
      <template #item.salepoint_id="{ item, index }">
        <v-autocomplete
          :value="item.salepoint_id"
          :items="getSalepointItemsFor(index)"
          :search-input.sync="searchInputs[index]"
          :loading="searchLoadings[index]"
          :readonly="readonly || !!item.id"
          :disabled="readonly"
          item-text="display_name"
          item-value="id"
          dense hide-details no-filter clearable
          placeholder="Code POS..."
          :no-data-text="searchInputs[index] ? 'Aucun résultat' : 'Tapez un code POS'"
          @update:search-input="onSalepointSearch(index, $event)"
          @input="onSalepointInput(index, $event)"
        />
      </template>

      <template #item.supervisor_id="{ item }">
        <v-autocomplete
          v-model="item.supervisor_id"
          :items="supervisorsOptions"
          item-text="display_name"
          item-value="id"
          dense hide-details
          :readonly="readonly"
          placeholder="ASM"
        />
      </template>

      <!-- Palier 1 : obligatoire -->
      <template #item.objective_1_qty="{ item }">
        <v-text-field
          v-model.number="item.objective_1_qty" type="number" min="1"
          dense hide-details :readonly="readonly"
        />
      </template>
      <template #item.objective_1_gain="{ item }">
        <v-text-field
          v-model.number="item.objective_1_gain" type="number" min="0"
          dense hide-details :readonly="readonly"
        />
      </template>

      <!-- Paliers 2 et 3 : facultatifs -->
      <template #item.objective_2_qty="{ item }">
        <v-text-field
          v-model.number="item.objective_2_qty" type="number" min="1"
          dense hide-details placeholder="—" :readonly="readonly"
        />
      </template>
      <template #item.objective_2_gain="{ item }">
        <v-text-field
          v-model.number="item.objective_2_gain" type="number" min="0"
          dense hide-details placeholder="—" :readonly="readonly"
        />
      </template>
      <template #item.objective_3_qty="{ item }">
        <v-text-field
          v-model.number="item.objective_3_qty" type="number" min="1"
          dense hide-details placeholder="—" :readonly="readonly"
        />
      </template>
      <template #item.objective_3_gain="{ item }">
        <v-text-field
          v-model.number="item.objective_3_gain" type="number" min="0"
          dense hide-details placeholder="—" :readonly="readonly"
        />
      </template>

      <template #item.hats_objective_qty="{ item }">
        <v-text-field
          v-model.number="item.hats_objective_qty" type="number" min="0"
          dense hide-details :readonly="readonly"
        />
      </template>

      <template #item.hats_gain="{ item }">
        <v-text-field
          v-model.number="item.hats_gain" type="number" min="0"
          dense hide-details :readonly="readonly"
        />
      </template>

      <template #item.actions="{ item, index }">
        <div class="d-flex align-center">
          <v-tooltip bottom v-if="rowErrors(item).length">
            <template #activator="{ on }">
              <v-icon small color="warning" class="mr-1" v-on="on">mdi-alert</v-icon>
            </template>
            <ul class="pl-3 ma-0">
              <li v-for="(e, i) in rowErrors(item)" :key="i">{{ e }}</li>
            </ul>
          </v-tooltip>

          <v-tooltip bottom v-if="!readonly && (item.visits_count || 0) === 0">
            <template #activator="{ on }">
              <v-btn icon small v-on="on" @click="removeRow(index, item)">
                <v-icon small color="error">mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Supprimer</span>
          </v-tooltip>
          <v-tooltip bottom v-else-if="(item.visits_count || 0) > 0">
            <template #activator="{ on }">
              <v-icon small color="grey" v-on="on">mdi-lock</v-icon>
            </template>
            <span>Affectation déjà visitée — non modifiable</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogBulk" max-width="520">
      <v-card>
        <v-card-title>Appliquer en masse</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text">
            Les valeurs renseignées écraseront celles de toutes les
            affectations non encore visitées. Les champs vides sont ignorés.
          </p>
          <v-row dense>
            <v-col cols="6"><v-text-field label="Obj. palier 1 *" v-model.number="bulk.objective_1_qty"  type="number" min="1" /></v-col>
            <v-col cols="6"><v-text-field label="Gain palier 1 *" v-model.number="bulk.objective_1_gain" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Obj. palier 2"   v-model.number="bulk.objective_2_qty"  type="number" min="1" /></v-col>
            <v-col cols="6"><v-text-field label="Gain palier 2"   v-model.number="bulk.objective_2_gain" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Obj. palier 3"   v-model.number="bulk.objective_3_qty"  type="number" min="1" /></v-col>
            <v-col cols="6"><v-text-field label="Gain palier 3"   v-model.number="bulk.objective_3_gain" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Obj. chapeau"    v-model.number="bulk.hats_objective_qty" type="number" min="0" /></v-col>
            <v-col cols="6"><v-text-field label="Gain chapeau"    v-model.number="bulk.hats_gain"        type="number" min="0" /></v-col>
          </v-row>
          <v-alert type="info" dense text class="mb-0 mt-2">
            Les quantités et les gains doivent croître du palier 1 au palier 3.
            Un palier ne peut pas être sauté.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogBulk = false">Annuler</v-btn>
          <v-btn color="primary" @click="applyBulk">Appliquer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { DEFAULT_VALUES, TIERS } from '@/constants/newTipV2';
import api from '@/api/newTipV2Api';

export default {
  name: 'NewTipV2TiersTable',

  props: {
    value: { type: Array, default: () => [] },
    supervisors: { type: Array, default: () => [] },
    knownSalepoints: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      rows: this.value.map(r => ({ ...r })),
      dialogBulk: false,
      bulk: {},
      // Cache des POS rencontrés, pour que v-autocomplete retrouve toujours
      // le label d'un id sélectionné après perte de focus. Doit rester
      // réactif → passer par this.$set pour ajouter des entrées.
      salepointsCache: {},
      searchInputs: [],
      searchResults: [],
      searchLoadings: [],
      searchTimers: [],
      tableHeaders: [
        { text: 'POS',          value: 'salepoint_id',     sortable: false, width: 240 },
        { text: 'ASM',          value: 'supervisor_id',    sortable: false, width: 190 },
        { text: 'Obj. P1 *',    value: 'objective_1_qty',  sortable: false, width: 90 },
        { text: 'Gain P1 *',    value: 'objective_1_gain', sortable: false, width: 90 },
        { text: 'Obj. P2',      value: 'objective_2_qty',  sortable: false, width: 90 },
        { text: 'Gain P2',      value: 'objective_2_gain', sortable: false, width: 90 },
        { text: 'Obj. P3',      value: 'objective_3_qty',  sortable: false, width: 90 },
        { text: 'Gain P3',      value: 'objective_3_gain', sortable: false, width: 90 },
        { text: 'Obj. chapeau', value: 'hats_objective_qty', sortable: false, width: 100 },
        { text: 'Gain chapeau', value: 'hats_gain',        sortable: false, width: 100 },
        { text: '',             value: 'actions',          sortable: false, width: 70 },
      ],
    };
  },

  computed: {
    supervisorsOptions() {
      return this.supervisors.map(s => ({
        ...s,
        display_name: `${s.name} · ${s.phone}`,
      }));
    },
    invalidRowCount() {
      return this.rows.filter(r => this.rowErrors(r).length > 0).length;
    },
  },

  watch: {
    value: {
      deep: true,
      handler(v) {
        if (JSON.stringify(v) !== JSON.stringify(this.rows)) {
          this.rows = v.map(r => ({ ...r }));
          this.syncRowState();
        }
      },
    },
    rows: {
      deep: true,
      handler(v) {
        this.$emit('input', v);
        this.$emit('validity', this.invalidRowCount === 0);
      },
    },
    knownSalepoints: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list)) return;
        list.forEach(sp => {
          if (sp && sp.id) {
            this.$set(this.salepointsCache, sp.id, this.formatSalepoint(sp));
          }
        });
      },
    },
  },

  mounted() {
    this.syncRowState();
  },

  methods: {
    syncRowState() {
      while (this.searchInputs.length < this.rows.length) this.searchInputs.push('');
      while (this.searchResults.length < this.rows.length) this.searchResults.push([]);
      while (this.searchLoadings.length < this.rows.length) this.searchLoadings.push(false);
      while (this.searchTimers.length < this.rows.length) this.searchTimers.push(null);
    },

    /**
     * Incohérences de paliers d'une ligne. Miroir de NewTipV2TierValidator
     * côté API — l'API reste l'autorité, ceci ne sert qu'à prévenir l'envoi
     * de lignes qui seront rejetées.
     */
    rowErrors(row) {
      const errors = [];
      const num = v => (v === null || v === undefined || v === '' ? null : Number(v));

      const tiers = TIERS.map(t => ({
        tier: t,
        qty: num(row[`objective_${t}_qty`]),
        gain: num(row[`objective_${t}_gain`]),
      }));

      // Palier 1 obligatoire
      if (tiers[0].qty === null || tiers[0].gain === null) {
        errors.push('Palier 1 obligatoire (quantité et gain).');
      }

      // Complétude et bornes de chaque palier
      tiers.forEach(t => {
        if (t.qty === null && t.gain === null) return;
        if (t.qty === null || t.gain === null) {
          errors.push(`Palier ${t.tier} incomplet (${t.qty === null ? 'quantité' : 'gain'} manquant).`);
          return;
        }
        if (t.qty < 1) errors.push(`Palier ${t.tier} : la quantité doit être au moins 1.`);
        if (t.gain < 0) errors.push(`Palier ${t.tier} : le gain ne peut pas être négatif.`);
      });

      if (errors.length) return errors;

      const defined = tiers.filter(t => t.qty !== null && t.gain !== null);

      // Contiguïté
      const expected = defined.map((_, i) => i + 1);
      if (defined.map(t => t.tier).join() !== expected.join()) {
        errors.push('Paliers non contigus : un palier ne peut pas être sauté.');
      }

      // Croissance stricte des quantités et des gains
      for (let i = 1; i < defined.length; i += 1) {
        const prev = defined[i - 1];
        const cur = defined[i];
        if (cur.qty <= prev.qty) {
          errors.push(`Objectif du palier ${cur.tier} (${cur.qty}) doit dépasser celui du palier ${prev.tier} (${prev.qty}).`);
        }
        if (cur.gain <= prev.gain) {
          errors.push(`Gain du palier ${cur.tier} (${cur.gain}) doit dépasser celui du palier ${prev.tier} (${prev.gain}).`);
        }
      }

      return errors;
    },

    formatSalepoint(sp) {
      return {
        id: sp.id,
        code: sp.code,
        name: sp.name,
        display_name: `${sp.code || '?'} · ${sp.name || ''}`,
      };
    },

    /**
     * Items du dropdown d'une ligne.
     *
     * La valeur sélectionnée DOIT toujours figurer dans la liste, sinon
     * Vuetify n'affiche plus son label après perte de focus (le POS semble
     * se réinitialiser).
     */
    getSalepointItemsFor(index) {
      const items = [];
      const seen = new Set();

      const currentId = this.rows[index] && this.rows[index].salepoint_id;
      if (currentId && this.salepointsCache[currentId]) {
        items.push(this.salepointsCache[currentId]);
        seen.add(currentId);
      }

      (this.searchResults[index] || []).forEach(sp => {
        if (!seen.has(sp.id)) {
          items.push(sp);
          seen.add(sp.id);
        }
      });

      return items;
    },

    onSalepointSearch(index, value) {
      const keyword = value || '';

      const currentId = this.rows[index] && this.rows[index].salepoint_id;
      const currentLabel = currentId && this.salepointsCache[currentId]
        ? this.salepointsCache[currentId].display_name
        : null;

      // L'utilisateur a juste cliqué dans le champ après sélection
      if (keyword === currentLabel) return;
      // Perte de focus : Vuetify vide search-input tout seul
      if (!keyword && currentId) return;

      if (this.searchTimers[index]) clearTimeout(this.searchTimers[index]);
      this.$set(this.searchTimers, index, setTimeout(() => {
        this.fetchSalepoints(index, keyword);
      }, 300));
    },

    async fetchSalepoints(index, keyword) {
      if (!keyword || keyword.length < 1) {
        this.$set(this.searchResults, index, []);
        return;
      }
      this.$set(this.searchLoadings, index, true);
      try {
        const { data } = await api.searchSalepoints(keyword, 10);
        const list = (data && data.data && data.data.data) || data.data || [];
        const formatted = list.map(sp => this.formatSalepoint(sp));
        formatted.forEach(sp => { this.$set(this.salepointsCache, sp.id, sp); });
        this.$set(this.searchResults, index, formatted);
      } catch (err) {
        console.error('searchSalepoints failed', err);
        this.$set(this.searchResults, index, []);
      } finally {
        this.$set(this.searchLoadings, index, false);
      }
    },

    onSalepointInput(index, newId) {
      if (!this.rows[index]) return;
      this.$set(this.rows[index], 'salepoint_id', newId);

      if (newId === null || newId === undefined) {
        this.$set(this.searchInputs, index, '');
        this.$set(this.searchResults, index, []);
      }
    },

    addRow() {
      this.rows.push({
        salepoint_id: null,
        supervisor_id: null,
        objective_1_qty: null,
        objective_1_gain: null,
        objective_2_qty: null,
        objective_2_gain: null,
        objective_3_qty: null,
        objective_3_gain: null,
        hats_objective_qty: DEFAULT_VALUES.hats_objective_qty,
        hats_gain: DEFAULT_VALUES.hats_gain,
        visits_count: 0,
      });
      this.syncRowState();
    },

    removeRow(index, item) {
      if (item.id) this.$emit('row-delete', item.id);
      this.rows.splice(index, 1);
      this.searchInputs.splice(index, 1);
      this.searchResults.splice(index, 1);
      this.searchLoadings.splice(index, 1);
      this.searchTimers.splice(index, 1);
    },

    applyBulk() {
      this.rows.forEach(r => {
        if ((r.visits_count || 0) > 0) return;
        Object.entries(this.bulk).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') {
            this.$set(r, k, v);
          }
        });
      });
      this.dialogBulk = false;
      this.bulk = {};
    },
  },
};
</script>

<style scoped>
.tiers-table >>> td { vertical-align: middle; }
.tiers-table >>> .v-text-field { padding-top: 0; margin-top: 0; }
</style>
